import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Printer, Ban } from 'lucide-react';
import { getCreditNote, voidCreditNote } from '../../api/creditNotes';
import { getInvoiceTemplate } from '../../api/platformBranding';
import { openInvoiceWindow, renderCreditNoteIntoWindow } from '../../lib/invoiceTemplate';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';

function money(value: string) {
  return `£${Number(value).toFixed(2)}`;
}

export function CreditNoteDetailPage() {
  const { creditNoteId } = useParams<{ creditNoteId: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const creditNoteQuery = useQuery({
    queryKey: ['credit-note', creditNoteId],
    queryFn: () => getCreditNote(creditNoteId!),
    enabled: !!creditNoteId,
  });

  const voidMutation = useMutation({
    mutationFn: () => voidCreditNote(creditNoteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-note', creditNoteId] });
      queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      showToast('Credit note voided.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not void the credit note.')),
  });

  async function handlePrint() {
    if (!creditNoteQuery.data) return;
    try {
      const win = openInvoiceWindow();
      const template = await getInvoiceTemplate();
      renderCreditNoteIntoWindow(win, creditNoteQuery.data, template);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not open the print window.', 'error');
    }
  }

  if (!creditNoteId) return null;

  if (creditNoteQuery.isLoading || !creditNoteQuery.data) {
    return (
      <div className="py-14 text-center">
        <Spinner />
      </div>
    );
  }

  const creditNote = creditNoteQuery.data;

  return (
    <div>
      <PageHeader
        breadcrumb={
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Link to="/credit-notes" className="hover:text-slate-600">
              Credit Notes
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{creditNote.customerOrganisation?.name}</span>
          </div>
        }
        title={`Credit note ${creditNote.creditNumber} - ${creditNote.customerOrganisation?.name ?? ''}`}
        description={creditNote.reason}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={creditNote.status === 'VOID' ? 'slate' : 'green'}>{creditNote.status}</Badge>
            <Button size="sm" variant="secondary" onClick={handlePrint}>
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </Button>
            {user?.isMspAdmin && creditNote.status !== 'VOID' && (
              <Button size="sm" variant="secondary" isLoading={voidMutation.isPending} onClick={() => voidMutation.mutate()}>
                <Ban className="h-3.5 w-3.5" /> Void
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {creditNote.invoice && (
        <p className="mb-4 text-sm text-slate-500">
          Against invoice{' '}
          <Link to={`/invoices/${creditNote.invoice.id}`} className="font-medium text-brand-700 hover:underline">
            {creditNote.invoice.invoiceNumber}
          </Link>
        </p>
      )}

      <Card>
        <Table>
          <Thead>
            <tr>
              <Th>Description</Th>
              <Th>Site</Th>
              <Th>Net</Th>
              <Th>VAT</Th>
              <Th>Total</Th>
            </tr>
          </Thead>
          <tbody>
            {creditNote.lines?.map((line) => (
              <Tr key={line.id}>
                <Td className="font-medium text-slate-900">{line.description}</Td>
                <Td>{line.site?.name ?? '-'}</Td>
                <Td>{money(line.netAmount)}</Td>
                <Td>{money(line.vatAmount)}</Td>
                <Td>{money(String(Number(line.netAmount) + Number(line.vatAmount)))}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <CardBody className="flex flex-col items-end gap-1 border-t border-slate-100 text-sm">
          <div className="flex w-48 justify-between">
            <span className="text-slate-500">Subtotal</span>
            <span className="text-slate-800">{money(creditNote.subtotal)}</span>
          </div>
          <div className="flex w-48 justify-between">
            <span className="text-slate-500">VAT</span>
            <span className="text-slate-800">{money(creditNote.vatTotal)}</span>
          </div>
          <div className="flex w-48 justify-between border-t border-slate-100 pt-1 font-semibold text-slate-900">
            <span>Total credited</span>
            <span>{money(creditNote.grandTotal)}</span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
